import React from 'react';
import { ArrowLeft, ArrowRight, Save, Rocket, Loader2 } from 'lucide-react';
import Button from '../../Button';

interface Props {
  currentStep: number;
  totalSteps: number;
  canGoBack: boolean;
  canGoNext: boolean;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  onLaunch?: () => void;
  saving: boolean;
  launching?: boolean;
  isStepValid: boolean;
  launchLabel?: string;
  launchingLabel?: string;
  launchIcon?: React.ReactNode;
  saveDraftLabel?: string;
  savingLabel?: string;
  /** Why Next is disabled, shown beside the button so the block is never silent */
  blockedReason?: string;
  /**
   * Hide Save/Launch here because the step itself presents them as primary choices.
   * Used by the creation wizard's Launch step, which offers the same actions as cards.
   */
  hidePrimaryActions?: boolean;
}

const WizardNavigation: React.FC<Props> = ({
  currentStep,
  totalSteps,
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  onSaveDraft,
  onLaunch,
  saving,
  launching = false,
  isStepValid,
  launchLabel,
  launchingLabel,
  launchIcon,
  saveDraftLabel,
  savingLabel,
  blockedReason,
  hidePrimaryActions = false
}) => {
  const isLastStep = currentStep === totalSteps;
  const progressPercent = totalSteps <= 1 ? 100 : Math.round(((currentStep - 1) / (totalSteps - 1)) * 100);

  return (
    <div className="border-t border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary p-4">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {/* Left: Back Button */}
        <div className="flex-1">
          {canGoBack && (
            <Button
              variant="secondary"
              onClick={onBack}
              disabled={saving || launching}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        {/* Center: Step Indicator */}
        <div className="flex-1 flex flex-col items-center">
          <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
            {progressPercent}% complete
          </span>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex-1 flex items-center justify-end gap-2">
          {!isStepValid && blockedReason && (
            <span className="text-xs text-amber-600 dark:text-amber-400 text-right max-w-[16rem]">
              {blockedReason}
            </span>
          )}

          {!hidePrimaryActions && (
            <Button
              variant="secondary"
              onClick={onSaveDraft}
              disabled={saving || launching}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {savingLabel || 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {saveDraftLabel || 'Save Draft'}
                </>
              )}
            </Button>
          )}

          {isLastStep ? (
            !hidePrimaryActions && onLaunch && (
              <Button
                variant="primary"
                onClick={onLaunch}
                disabled={!isStepValid || launching}
              >
                {launching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {launchingLabel || 'Launching...'}
                  </>
                ) : (
                  <>
                    {launchIcon || <Rocket className="w-4 h-4 mr-2" />}
                    {launchLabel || 'Launch Now'}
                  </>
                )}
              </Button>
            )
          ) : (
            <Button
              variant="primary"
              onClick={onNext}
              disabled={!canGoNext || !isStepValid || saving || launching}
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardNavigation;

